// ============================================================
// THE READ MARK - what is new since this viewer last looked (v9.7.0).
//
// The log hung wherever it was scrolled, and nothing said that a line
// had arrived: only the vox counted as unread. A viewer now has a mark
// per session - the time of the newest line they had on screen while
// the log was open and at its foot - and everything after it that
// somebody ELSE wrote is new: a divider is drawn before the first such
// line, and each lane says how many it holds.
//
// Pure, zero React dependency.
// ============================================================
import type { LogItem } from '../chat/merge';
import { LANES, laneOf, type Lane } from './lanes';

/** Who is reading: their account for vox lines, their name for narrative lines. */
export interface Reader {
  uid: string | null;
  name: string | null;
}

/** Did this viewer write the line? Their own lines are never news to them. */
function mine(item: LogItem, reader: Reader): boolean {
  if (item.kind === 'chat') return !!reader.uid && item.msg.fromUid === reader.uid;
  const writer = (item.entry as { userName?: unknown }).userName;
  return !!reader.name && writer === reader.name;
}

/** The lines after the mark that somebody else wrote, in order. */
export function unseenSince<T extends LogItem>(items: readonly T[], readAt: number, reader: Reader): T[] {
  return items.filter((item) => item.ts > readAt && !mine(item, reader));
}

/** How many of them each lane holds. */
export function countsByLane(unseen: readonly LogItem[]): Record<Lane, number> {
  const counts = Object.fromEntries(LANES.map((lane) => [lane, 0])) as Record<Lane, number>;
  for (const item of unseen) counts[laneOf(item)] += 1;
  return counts;
}

/** Where the divider goes: the index of the first unseen line in `items`, or -1 when nothing is new. */
export function firstUnseenIndex(items: readonly LogItem[], readAt: number, reader: Reader): number {
  return items.findIndex((item) => item.ts > readAt && !mine(item, reader));
}

/** The mark once everything on screen has been read: the newest line's time, never moving back. */
export function markAfter(items: readonly LogItem[], readAt: number): number {
  return items.reduce((latest, item) => Math.max(latest, item.ts), readAt);
}
